/**
 * /admin/comics — Admin comics management.
 *
 * Upload new comics, edit title/description, set current, soft/hard delete.
 * Follows the same patterns as AdminTracksPage.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePageTitle } from '../../hooks/usePageTitle.js';
import {
  getAdminComics,
  uploadComic,
  updateComic,
  setCurrentComic,
  deleteComic,
} from '../../api/comics.js';
import './AdminComicsPage.css';


export default function AdminComicsPage() {
  usePageTitle('Admin · Comics');

  const [comics, setComics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Upload form state
  const [uploading, setUploading] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const fileRef = useRef(null);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminComics();
      setComics(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Upload ──

  function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadFile(file);

    // Generate preview
    const reader = new FileReader();
    reader.onload = (ev) => setUploadPreview(ev.target.result);
    reader.readAsDataURL(file);

    // Auto-fill title from filename if empty
    if (!uploadTitle) {
      const name = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
      setUploadTitle(name);
    }
  }

  async function handleUpload(e) {
    e.preventDefault();
    if (!uploadFile || !uploadTitle.trim()) return;

    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', uploadFile);
      form.append('title', uploadTitle.trim());
      form.append('description', uploadDesc.trim());
      form.append('publish', 'true');

      await uploadComic(form);

      // Reset form
      setUploadTitle('');
      setUploadDesc('');
      setUploadFile(null);
      setUploadPreview(null);
      if (fileRef.current) fileRef.current.value = '';

      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  }

  // ── Set current ──

  async function handleSetCurrent(id) {
    try {
      await setCurrentComic(id);
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  // ── Edit ──

  function startEdit(comic) {
    setEditingId(comic.id);
    setEditTitle(comic.title);
    setEditDesc(comic.description || '');
  }

  async function saveEdit() {
    try {
      await updateComic(editingId, {
        title: editTitle.trim(),
        description: editDesc.trim(),
      });
      setEditingId(null);
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  // ── Delete ──

  async function handleDelete(id, hard = false) {
    const msg = hard
      ? 'Permanently delete this comic and all its reactions? This cannot be undone.'
      : 'Archive this comic? It will be hidden from the public page.';
    if (!confirm(msg)) return;

    try {
      await deleteComic(id, hard);
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  // ── Render ──

  const published = comics.filter((c) => c.status === 'published');
  const drafts = comics.filter((c) => c.status === 'draft');
  const archived = comics.filter((c) => c.status === 'archived');

  return (
    <div className="adm-comics">
      <h1 className="adm-comics__title">Comics</h1>
      <p className="adm-comics__subtitle">
        {published.length} published · {drafts.length} draft · {archived.length} archived
      </p>

      {error && <div className="adm-comics__error">{error}</div>}

      {/* ── Upload form ── */}
      <section className="adm-comics__upload">
        <h2 className="adm-comics__section-label">upload new comic</h2>
        <form onSubmit={handleUpload} className="adm-comics__upload-form">
          <div className="adm-comics__dropzone" onClick={() => fileRef.current?.click()}>
            {uploadPreview ? (
              <img src={uploadPreview} alt="Preview" className="adm-comics__preview-img" />
            ) : (
              <div className="adm-comics__dropzone-text">
                click to select an image
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          <div className="adm-comics__upload-fields">
            <input
              type="text"
              placeholder="Title"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              className="adm-comics__input"
              required
            />
            <textarea
              placeholder="Description (optional)"
              value={uploadDesc}
              onChange={(e) => setUploadDesc(e.target.value)}
              className="adm-comics__textarea"
              rows={2}
            />
            <button
              type="submit"
              disabled={!uploadFile || !uploadTitle.trim() || uploading}
              className="adm-comics__btn adm-comics__btn--primary"
            >
              {uploading ? 'uploading...' : 'upload & publish'}
            </button>
          </div>
        </form>
      </section>

      {/* ── Comics list ── */}
      {loading ? (
        <div className="adm-comics__loading">loading...</div>
      ) : (
        <section className="adm-comics__list">
          <h2 className="adm-comics__section-label">all comics</h2>
          {comics.length === 0 && (
            <div className="adm-comics__empty">no comics yet. upload the first one above.</div>
          )}
          {comics.map((c) => (
            <div
              key={c.id}
              className={`adm-comics__card ${c.is_current ? 'adm-comics__card--current' : ''} ${c.status === 'archived' ? 'adm-comics__card--archived' : ''}`}
            >
              <div className="adm-comics__card-thumb">
                <img src={c.thumb_path || c.file_path} alt={c.title} />
              </div>

              <div className="adm-comics__card-body">
                {editingId === c.id ? (
                  <div className="adm-comics__edit-form">
                    <input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="adm-comics__input"
                    />
                    <textarea
                      value={editDesc}
                      onChange={(e) => setEditDesc(e.target.value)}
                      className="adm-comics__textarea"
                      rows={2}
                    />
                    <div className="adm-comics__edit-actions">
                      <button onClick={saveEdit} className="adm-comics__btn adm-comics__btn--primary">save</button>
                      <button onClick={() => setEditingId(null)} className="adm-comics__btn">cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="adm-comics__card-header">
                      <span className="adm-comics__card-title">{c.title}</span>
                      <div className="adm-comics__badges">
                        {c.is_current && <span className="adm-comics__badge adm-comics__badge--current">current</span>}
                        <span className={`adm-comics__badge adm-comics__badge--${c.status}`}>{c.status}</span>
                      </div>
                    </div>
                    {c.description && (
                      <p className="adm-comics__card-desc">{c.description}</p>
                    )}
                    <div className="adm-comics__card-meta">
                      <span>{c.original_filename}</span>
                      {c.width && c.height && <span>{c.width}×{c.height}</span>}
                      {c.file_size && <span>{(c.file_size / 1024).toFixed(0)} KB</span>}
                    </div>
                  </>
                )}
              </div>

              {editingId !== c.id && (
                <div className="adm-comics__card-actions">
                  {!c.is_current && c.status === 'published' && (
                    <button onClick={() => handleSetCurrent(c.id)} className="adm-comics__btn adm-comics__btn--accent" title="Set as featured">
                      ★ feature
                    </button>
                  )}
                  <button onClick={() => startEdit(c)} className="adm-comics__btn" title="Edit">
                    edit
                  </button>
                  {c.status !== 'archived' && (
                    <button onClick={() => handleDelete(c.id, false)} className="adm-comics__btn adm-comics__btn--warn" title="Archive">
                      archive
                    </button>
                  )}
                  <button onClick={() => handleDelete(c.id, true)} className="adm-comics__btn adm-comics__btn--danger" title="Permanently delete">
                    delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
